import React, { useEffect, useState } from 'react';
import Dialog from "../../components/controls/Dialog";
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import * as llmModelService from '../../services/llmModelService';
import * as llmProviderService from '../../services/llmProviderService';
import Input from '../../components/controls/Input';
import { useForm } from '../../components/useForm';
import useAlert from '../../components/useAlert';
import { FormControl, InputLabel, MenuItem, Select as MuiSelect, FormGroup, FormControlLabel, Checkbox, Box } from '@mui/material';

const initialFValues = {
    id: null,
    provider: { id: '' },
    name: '',
    version: '',
    priceCzkPerInputToken: '',
    priceCzkPerOutputToken: '',
    maxTokensParamFormat: 'max_tokens',
    thinkingConfig: {
        isThinking: false,
        canDisable: false,
        paramType: 'BUDGET',
        budgetMin: '',
        budgetMax: '',
        thinkingLevels: ''
    }
}

const LlmModelDialog = (props) => {
    const { open, selectedRow, getLlmModels } = props;
    const [providers, setProviders] = useState([]);

    const {
        values,
        setValues,
        errors,
        handleInputChange,
        handleClose,
        handleError
    } = useForm(initialFValues, props);

    useEffect(() => {
        llmProviderService.getLlmProviders().then(response => {
            setProviders(response.data.items || []);
        });
    }, []);

    useEffect(() => {
        if (open)
            handleAlertClose();
        if (selectedRow) {
            setValues({
                ...selectedRow,
                priceCzkPerInputToken: selectedRow.priceCzkPerInputToken ?? '',
                priceCzkPerOutputToken: selectedRow.priceCzkPerOutputToken ?? '',
                thinkingConfig: {
                    ...initialFValues.thinkingConfig,
                    ...selectedRow.thinkingConfig,
                }
            });
        } else {
            setValues(initialFValues);
        }
        //eslint-disable-next-line
    }, [selectedRow, open])

    const {
        Alert,
        status,
        showSuccess,
        showError,
        handleAlertClose
    } = useAlert();

    const handleProviderChange = (e) => {
        const { value } = e.target;
        setValues({
            ...values,
            provider: { id: value }
        });
    }

    const handleThinkingChange = (e) => {
        const { checked, name, type, value } = e.target;
        const fieldValue = type === 'checkbox' ? checked : value;

        setValues({
            ...values,
            thinkingConfig: {
                ...values.thinkingConfig,
                [name]: fieldValue
            }
        });
    }

    const handleThinkingLevelsChange = (e) => {
        const { value } = e.target;
        setValues({
            ...values,
            thinkingConfig: {
                ...values.thinkingConfig,
                thinkingLevels: value
            }
        });
    }

    const submit = () => {
        if (validate()) {
            const thinkingConfig = {
                ...values.thinkingConfig,
                budgetMin: values.thinkingConfig.budgetMin === '' ? null : parseInt(values.thinkingConfig.budgetMin),
                budgetMax: values.thinkingConfig.budgetMax === '' ? null : parseInt(values.thinkingConfig.budgetMax),
                thinkingLevels: values.thinkingConfig.thinkingLevels ? values.thinkingConfig.thinkingLevels.split(',').map(s => s.trim()) : null
            };

            const payload = {
                ...values,
                priceCzkPerInputToken: values.priceCzkPerInputToken === '' ? null : parseFloat(values.priceCzkPerInputToken),
                priceCzkPerOutputToken: values.priceCzkPerOutputToken === '' ? null : parseFloat(values.priceCzkPerOutputToken),
                thinkingConfig: values.thinkingConfig.isThinking ? thinkingConfig : { isThinking: false }
            };

            if (selectedRow)
                llmModelService.updateLlmModel(selectedRow.id, payload)
                    .then(() => {
                        showSuccess();
                        handleClose();
                        getLlmModels();
                    })
                    .catch(error => {
                        showError(error.response?.data?.message || "Error updating LLM Model");
                        handleError(error.response?.data?.fields);
                    });
            else
                llmModelService.createLlmModel(payload)
                    .then(() => {
                        showSuccess();
                        handleClose();
                        getLlmModels();
                    })
                    .catch(error => {
                        showError(error.response?.data?.message || "Error creating LLM Model");
                        handleError(error.response?.data?.fields);
                    });
        }
    };

    const validate = () => {
        let temp = {}
        temp.provider = values.provider.id ? "" : "Provider is required";
        temp.name = values.name ? "" : "Name is required";
        temp.version = values.version ? "" : "Version is required";
        temp.maxTokensParamFormat = values.maxTokensParamFormat ? "" : "Max tokens param format is required";
        return Object.values(temp).every(x => x === '');
    }

    return (
        <React.Fragment>
            {status ? <Alert /> : null}
            <Dialog
                title={selectedRow ? "Edit LLM Model" : "Create new LLM Model"}
                open={open}
                handleClose={handleClose}>
                <DialogContent>
                    <FormControl fullWidth size="small" sx={{ marginTop: 2 }}>
                        <InputLabel>Provider</InputLabel>
                        <MuiSelect
                            label="Provider"
                            name="provider"
                            value={providers.some(p => p.id === values.provider.id) ? values.provider.id : (providers[0]?.id || '')}
                            onChange={handleProviderChange}
                            error={!!errors.provider}
                        >
                            {providers.map(p => (
                                <MenuItem key={p.id} value={p.id}>{p.name} ({p.id})</MenuItem>
                            ))}
                        </MuiSelect>
                    </FormControl>
                    <Input
                        label="Model Name"
                        name="name"
                        defValue={values.name}
                        onChange={handleInputChange}
                        error={errors.name}
                    />
                    <Input
                        label="Version"
                        name="version"
                        defValue={values.version}
                        onChange={handleInputChange}
                        error={errors.version}
                    />
                    <Input
                        label="Price CZK per Input Token"
                        name="priceCzkPerInputToken"
                        type="number"
                        defValue={values.priceCzkPerInputToken}
                        onChange={handleInputChange}
                        error={errors.priceCzkPerInputToken}
                    />
                    <Input
                        label="Price CZK per Output Token"
                        name="priceCzkPerOutputToken"
                        type="number"
                        defValue={values.priceCzkPerOutputToken}
                        onChange={handleInputChange}
                        error={errors.priceCzkPerOutputToken}
                    />
                    <Input
                        label="Max Tokens Param Format"
                        name="maxTokensParamFormat"
                        defValue={values.maxTokensParamFormat}
                        onChange={handleInputChange}
                        error={errors.maxTokensParamFormat}
                    />
                    <FormGroup sx={{ marginTop: 2 }}>
                        <FormControlLabel
                            control={<Checkbox checked={values.thinkingConfig.isThinking} onChange={handleThinkingChange} name="isThinking" />}
                            label="Is Thinking Model"
                        />
                    </FormGroup>
                    {values.thinkingConfig.isThinking && (
                        <Box sx={{ marginLeft: 4 }}>
                            <FormGroup>
                                <FormControlLabel
                                    control={<Checkbox checked={values.thinkingConfig.canDisable ?? false} onChange={handleThinkingChange} name="canDisable" />}
                                    label="Can Disable Thinking"
                                />
                            </FormGroup>
                            <FormControl fullWidth size="small" sx={{ marginTop: 2 }}>
                                <InputLabel>Thinking Param Type</InputLabel>
                                <MuiSelect
                                    label="Thinking Param Type"
                                    name="paramType"
                                    value={values.thinkingConfig.paramType || 'BUDGET'}
                                    onChange={handleThinkingChange}
                                >
                                    <MenuItem value="BUDGET">BUDGET</MenuItem>
                                    <MenuItem value="LEVEL">LEVEL</MenuItem>
                                </MuiSelect>
                            </FormControl>
                            {values.thinkingConfig.paramType === 'BUDGET' && (
                                <Box>
                                    <Input
                                        fullWidth
                                        label="Budget Min"
                                        name="budgetMin"
                                        type="number"
                                        value={values.thinkingConfig.budgetMin}
                                        onChange={handleThinkingChange}
                                    />
                                    <Input
                                        fullWidth
                                        label="Budget Max"
                                        name="budgetMax"
                                        type="number"
                                        value={values.thinkingConfig.budgetMax}
                                        onChange={handleThinkingChange}
                                    />
                                </Box>
                            )}
                            {values.thinkingConfig.paramType === 'LEVEL' && (
                                <Input
                                    fullWidth
                                    label="Thinking Levels (comma separated)"
                                    name="thinkingLevels"
                                    value={Array.isArray(values.thinkingConfig.thinkingLevels) ? values.thinkingConfig.thinkingLevels.join(', ') : (values.thinkingConfig.thinkingLevels || '')}
                                    onChange={handleThinkingLevelsChange}
                                />
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleClose}>Cancel</Button>
                    <Button variant="contained" onClick={submit}>Save</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    )
}

export default LlmModelDialog;
